import type { DocSection } from "./legalDocUtils";
import { addDays, parseLocalDate, daysInMonth, type YMD } from "@/tools/converters/dates";

/**
 * Pure builders for the lease, power-of-attorney and eviction-notice documents, so the wording and the
 * date / notice-period rules can be unit-tested. All of these are general US templates, not legal advice.
 */

export const US_STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", "Delaware", "District of Columbia", "Florida",
  "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts",
  "Michigan", "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey", "New Mexico",
  "New York", "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina",
  "South Dakota", "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming",
] as const;

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
export const longDate = (d: YMD) => `${MONTHS[d.m - 1]} ${d.d}, ${d.y}`;
export const money = (v: string) => {
  const n = Number(v.replace(/,/g, ""));
  return Number.isFinite(n) ? n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : v;
};

// ---------------------------------------------------------------------------------------------
// lease
// ---------------------------------------------------------------------------------------------

/** Last day of a lease that starts on `start` and runs `months` months (start + months, minus one day). */
export function leaseEndDate(start: YMD, months: number): YMD {
  const total = start.y * 12 + (start.m - 1) + months;
  const y = Math.floor(total / 12), m = (total % 12) + 1;
  const d = Math.min(start.d, daysInMonth(y, m));
  return addDays({ y, m, d }, -1);
}

export interface LeaseInput {
  landlord: string; tenant: string; address: string; state: string;
  rent: string; deposit: string; termMonths: number; startDate: string; // yyyy-mm-dd
  rentDueDay: string; lateFee: string; graceDays: string;
  afterTerm: "monthly" | "ends"; noticeDays: string; entryHours: string;
  utilities: string; pets: "none" | "consent"; smoking: boolean; occupants: string; depositReturnDays: string;
  builtBefore1978: boolean; signedOn: string; // already-formatted long date
}

export function buildLeaseSections(i: LeaseInput): DocSection[] {
  const start = parseLocalDate(i.startDate);
  const end = start ? leaseEndDate(start, i.termMonths) : null;
  const S = (v: string, ph: string) => v.trim() || ph;
  const sections: DocSection[] = [
    { paragraphs: [`This Lease Agreement is made on ${i.signedOn}, between ${S(i.landlord, "[Landlord Name]")} ("Landlord") and ${S(i.tenant, "[Tenant Name]")} ("Tenant") for the property located at ${S(i.address, "[Property Address]")} (the "Property").`] },
    {
      heading: "1. Term",
      paragraphs: [
        `The lease term begins on ${start ? longDate(start) : "[start date]"} and ends on ${end ? longDate(end) : "[end date]"} (${i.termMonths} months).`,
        i.afterTerm === "monthly"
          ? `If Tenant remains in the Property after the term ends with Landlord's consent, the tenancy continues month to month on the same terms. Either party may end a month-to-month tenancy by giving the other at least ${S(i.noticeDays, "30")} days' written notice, or any longer notice the law requires.`
          : `This lease ends on the date above and does not renew automatically. Tenant must vacate by the end date unless the parties sign a new agreement or written extension.`,
      ],
    },
    {
      heading: "2. Rent",
      paragraphs: [
        `Tenant agrees to pay $${money(i.rent)} per month, due on day ${S(i.rentDueDay, "1")} of each month.`,
        i.lateFee.trim() && Number(i.lateFee) > 0
          ? `If rent is not received within ${S(i.graceDays, "5")} day(s) after it is due, Tenant shall pay a late fee of $${money(i.lateFee)}, to the extent permitted by law.`
          : "Late payments may be subject to fees only as permitted by law.",
      ],
    },
    { heading: "3. Security Deposit", paragraphs: [`Tenant shall pay a security deposit of $${money(i.deposit)}. Landlord will return the deposit, less any deductions permitted by law, with an itemized statement, within ${S(i.depositReturnDays, "30")} days after Tenant moves out and returns possession, or within any shorter period the law of ${S(i.state, "the state where the Property is located")} requires.`] },
    { heading: "4. Use of Property and Occupants", paragraphs: [
      "The Property shall be used solely as a private residence. Subletting or assigning this lease requires prior written consent from Landlord.",
      i.occupants.trim() ? `Persons permitted to live in the Property: ${i.occupants.trim()}.` : "Only Tenant and the persons named in this lease may live in the Property.",
    ] },
    { heading: "5. Utilities", paragraphs: [i.utilities.trim() ? i.utilities.trim() : "Tenant pays for all utilities and services used at the Property, except those Landlord agrees in writing to provide."] },
    { heading: "6. Pets and Smoking", paragraphs: [
      i.pets === "none" ? "No pets are allowed without Landlord's prior written consent. This does not apply to service animals or assistance animals as defined by law." : "Pets are allowed only with Landlord's prior written consent, which may include an additional deposit or fee as the law allows. Service animals and assistance animals are governed by law.",
      i.smoking ? "Smoking is permitted in the Property." : "Smoking is not permitted inside the Property.",
    ] },
    { heading: "7. Landlord's Entry", paragraphs: [`Landlord may enter the Property after giving at least ${S(i.entryHours, "24")} hours' notice, at reasonable times, for repairs, inspections or showings. In an emergency Landlord may enter without notice.`] },
    { heading: "8. Maintenance", paragraphs: ["Tenant agrees to keep the Property clean and in good condition and to report needed repairs promptly. Landlord is responsible for major repairs and for keeping the Property fit to live in, except damage caused by Tenant or Tenant's guests."] },
    { heading: "9. Governing Law", paragraphs: [`This Agreement is governed by the laws of ${S(i.state, "[State]")} and all applicable local landlord-tenant laws.`] },
  ];
  if (i.builtBefore1978) {
    sections.push({
      heading: "10. Lead-Based Paint Disclosure (housing built before 1978)",
      paragraphs: [
        "Lead Warning Statement: Housing built before 1978 may contain lead-based paint. Lead from paint, paint chips and dust can pose health hazards if not managed properly. Lead exposure is especially harmful to young children and pregnant women. Before renting pre-1978 housing, lessors must disclose the presence of known lead-based paint and lead-based paint hazards in the dwelling. Lessees must also receive a federally approved pamphlet on lead poisoning prevention.",
        "Landlord's disclosure (check one):  [  ] Landlord knows of lead-based paint or lead-based paint hazards in the Property (explain): ____________________   [  ] Landlord has no knowledge of lead-based paint or lead-based paint hazards in the Property.",
        "Records and reports (check one):  [  ] Landlord has given Tenant all available records and reports about lead-based paint in the Property (list): ____________________   [  ] Landlord has no such records or reports.",
        "Tenant's acknowledgment (initial):  ______ Tenant has received copies of all information listed above.   ______ Tenant has received the pamphlet \"Protect Your Family From Lead in Your Home.\"",
        "Certification of accuracy: The parties have reviewed the information above and certify, to the best of their knowledge, that it is true and accurate.",
        "Landlord: _________________________  Date: _____________",
        "Tenant: _________________________  Date: _____________",
      ],
    });
  }
  sections.push({
    heading: "Signatures",
    paragraphs: ["Landlord: _________________________  Date: _____________", "Tenant: _________________________  Date: _____________"],
  });
  return sections;
}

// ---------------------------------------------------------------------------------------------
// power of attorney
// ---------------------------------------------------------------------------------------------

export interface PoaInput {
  principal: string; agent: string; alternateAgent: string; powers: string; state: string;
  expiration: string; // yyyy-mm-dd or ""
  durable: boolean; springing: boolean; signedOn: string;
}

export function buildPoaSections(i: PoaInput): DocSection[] {
  const S = (v: string, ph: string) => v.trim() || ph;
  const exp = parseLocalDate(i.expiration);
  const durability = i.durable
    ? "This Power of Attorney shall not be affected by my later disability or incapacity. It remains effective even if I become incapacitated."
    : "This Power of Attorney is not durable: it ends if I become incapacitated.";
  const effective = i.springing && i.durable
    ? "This Power of Attorney becomes effective only if I become incapacitated, as certified in writing by my attending physician or by two licensed physicians (a \"springing\" power). Until then my Agent has no authority under it."
    : `This Power of Attorney is effective immediately on ${i.signedOn}.`;
  return [
    { paragraphs: [`I, ${S(i.principal, "[Principal Name]")} ("Principal"), residing in ${S(i.state, "[State]")}, appoint ${S(i.agent, "[Agent Name]")} ("Agent") as my attorney-in-fact.${i.alternateAgent.trim() ? ` If my Agent is unable or unwilling to act, I appoint ${i.alternateAgent.trim()} as my alternate Agent.` : ""}`] },
    { heading: "1. Grant of Authority", paragraphs: [`I grant my Agent authority to act for me in connection with the following matters: ${S(i.powers, "[powers]")}. This authority includes the power to sign documents, endorse and deposit checks, and take any action I could take personally for these matters.`] },
    { heading: "2. Durability and When It Takes Effect", paragraphs: [durability, effective] },
    {
      heading: "3. Duration",
      paragraphs: [exp ? `This Power of Attorney ends on ${longDate(exp)}, unless I revoke it earlier in writing.` : `This Power of Attorney continues until I revoke it in writing or until my death${i.durable ? "" : " or incapacity"}.`],
    },
    { heading: "4. Agent's Duties", paragraphs: ["My Agent must act in my best interest, keep my property separate from the Agent's own, keep records of all transactions made for me, and not make gifts or change beneficiaries unless this document expressly allows it."] },
    { heading: "5. Revocation", paragraphs: ["I may revoke this Power of Attorney at any time by written notice to my Agent and to any institution that has relied on it."] },
    { heading: "6. Governing Law", paragraphs: [`This document is governed by the laws of ${S(i.state, "[State]")}. Execution requirements (notarization, witnesses, statutory wording) vary by state.`] },
    {
      heading: "Signature of Principal",
      paragraphs: [`Signed on ${i.signedOn}.`, "Principal: _________________________  Date: _____________"],
    },
    {
      heading: "Agent's Acceptance",
      paragraphs: ["I accept this appointment and agree to act as Agent in the Principal's best interest.", "Agent: _________________________  Date: _____________"],
    },
    {
      heading: "Witnesses (if required by your state)",
      paragraphs: ["Witness 1: ________________________  Date: ____________", "Printed name: ________________________", "Witness 2: ________________________  Date: ____________", "Printed name: ________________________"],
    },
    {
      heading: "Notary Acknowledgment",
      paragraphs: [
        "State of ____________________     County of ____________________",
        "On this ______ day of ________________, 20____, before me, the undersigned notary public, personally appeared ______________________________, known to me or satisfactorily proven to be the person whose name is signed on this document, and acknowledged that he or she signed it voluntarily for its stated purpose.",
        "Notary Public: _________________________   My commission expires: _____________   (Seal)",
      ],
    },
  ];
}

// ---------------------------------------------------------------------------------------------
// eviction notice
// ---------------------------------------------------------------------------------------------

export type NoticeType = "pay-or-quit" | "cure-or-quit" | "unconditional-quit";

export interface MinNotice { days: number; note: string }

/**
 * Minimum notice, in days, for a pay-or-quit notice, for the states we have a source for.
 * Other states are not guessed: the page tells the landlord to check the state's rule.
 * These come from the QA review of this tool (state landlord-tenant statutes); verify before serving.
 */
const PAY_OR_QUIT_MIN: Record<string, MinNotice> = {
  California: { days: 3, note: "excluding weekends and court holidays" },
  Texas: { days: 3, note: "notice to vacate, unless the lease sets a different period" },
  Florida: { days: 3, note: "excluding weekends and legal holidays" },
  "New York": { days: 14, note: "" },
  Washington: { days: 14, note: "" },
  // Ohio R.C. 1923.04(A): notice to leave "three or more days before beginning the action" (codes.ohio.gov, checked 25 Oct 2026).
  Ohio: { days: 3, note: "" },
};

const dayWord = (n: number) => `${n} day${n === 1 ? "" : "s"}`;

export function minNotice(state: string, type: NoticeType): MinNotice | null {
  if (type !== "pay-or-quit") return null;
  return PAY_OR_QUIT_MIN[state] ?? null;
}

export function statesWithKnownMinimums(): string[] {
  return Object.keys(PAY_OR_QUIT_MIN);
}

/** An error for a too-short period, or null. */
export function noticePeriodProblem(state: string, type: NoticeType, days: number): string | null {
  if (!Number.isFinite(days) || days < 1 || !Number.isInteger(days)) return "Enter a whole number of days (1 or more).";
  const m = minNotice(state, type);
  if (m && days < m.days) return `${state} requires at least ${m.days} days for a pay-or-quit notice${m.note ? ` (${m.note})` : ""}. A shorter notice can get your case dismissed.`;
  return null;
}

export const NOTICE_TYPES: { value: NoticeType; label: string; text: string }[] = [
  { value: "pay-or-quit", label: "Pay Rent or Quit", text: "You are in default for failure to pay rent." },
  { value: "cure-or-quit", label: "Cure or Quit (lease violation)", text: "You are in violation of the terms of your lease agreement." },
  { value: "unconditional-quit", label: "Unconditional Quit (no cure option)", text: "Your tenancy is being terminated." },
];

export interface EvictionInput {
  landlord: string; tenant: string; address: string; type: NoticeType; amountOwed: string; days: number; violation: string; dateText: string;
  /** Optional: lets the notice print the state's weekend / holiday counting rule. */
  state?: string;
}

export function buildEvictionSections(i: EvictionInput): DocSection[] {
  const t = NOTICE_TYPES.find(x => x.value === i.type) ?? NOTICE_TYPES[0];
  const S = (v: string, ph: string) => v.trim() || ph;
  const excl = i.state ? PAY_OR_QUIT_MIN[i.state]?.note ?? "" : "";
  const countRule = i.type === "pay-or-quit" && excl ? ` (${excl})` : "";
  const d = dayWord(i.days);
  return [
    { paragraphs: [`To: ${S(i.tenant, "[Tenant Name]")}`, `Property Address: ${S(i.address, "[Property Address]")}`, `From: ${S(i.landlord, "[Landlord Name]")}`, `Date: ${i.dateText}`] },
    {
      heading: t.label.toUpperCase(),
      paragraphs: [
        t.text,
        ...(i.type === "cure-or-quit" && i.violation.trim() ? [`The violation is: ${i.violation.trim()}`] : []),
        i.type === "pay-or-quit"
          ? `The amount currently owed is $${money(S(i.amountOwed, "0"))}. You have ${d}${countRule} from receipt of this notice to pay the amount owed in full or vacate the premises. The last day to comply is: ____________________ (fill in after the notice is served).`
          : i.type === "cure-or-quit"
          ? `You have ${d} from receipt of this notice to correct this violation or vacate the premises. The last day to comply is: ____________________ (fill in after the notice is served).`
          : `You are required to vacate the premises within ${d} of receipt of this notice. The last day to vacate is: ____________________ (fill in after the notice is served).`,
      ],
    },
    { heading: "Consequences of Non-Compliance", paragraphs: ["If you do not comply within the stated period, legal proceedings to recover possession of the property may be started against you, which may include additional costs and fees as permitted by law."] },
    { heading: "Signature", paragraphs: ["Landlord/Agent: _________________________  Date: _____________"] },
    {
      heading: "Proof of Service",
      paragraphs: [
        "I served this notice on the tenant named above as follows (check one):",
        "[  ] Delivered to the tenant in person      [  ] Left with a person of suitable age at the property      [  ] Posted at the property and mailed      [  ] Sent by certified mail",
        "Date served: _______________   Time: ___________   Served by (printed name): ______________________",
        "Signature of person serving: _________________________",
      ],
    },
  ];
}

/** Today's date (local, not UTC) as yyyy-mm-dd, for date inputs. */
export function todayIso(now = new Date()): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}
